import type { MouseEventHandler } from 'react';
import { useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import AssistantOutlined from '@mui/icons-material/AssistantOutlined';

import Button from '@/components/Buttons/Button';
import SideDrawer from '@/components/SideDrawer';
import Chat from '@/features/AIChat/AskGXDrawer/Chat/Chat';
import ChatHistory from '@/features/AIChat/AskGXDrawer/ChatHistory';
import MessageInput from '@/features/AIChat/AskGXDrawer/MessageInput/MessageInput';
import { ChatProvider } from '@/features/AIChat/context/ChatProvider';
import { useChat } from '@/features/AIChat/context/useChat';

const AskGXContent = ({ open, onClose }: { open: boolean; onClose: MouseEventHandler }) => {
  const { conversationId, setConversationId, messages } = useChat();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const historyBtnRef = useRef<HTMLElement | null>(null);

  const handleHistoryClick = () => {
    const isOpen = Boolean(anchorEl);
    if (isOpen) {
      setAnchorEl(null);
    } else if (historyBtnRef.current) {
      setAnchorEl(historyBtnRef.current);
    }
  };

  return (
    <SideDrawer
      headerAction={
        <Button
          color='primary'
          disabled={messages.length === 0 && conversationId !== null}
          size='large'
          sx={{
            '&.Mui-disabled': {
              backgroundColor: 'var(--GX-Text-Disabled, #6C737A)',
              color: 'var(--GX-Neutral, #4F5458)',
              opacity: 1
            },
            width: '144px',
            height: '36px',
            padding: '4px 12px'
          }}
          variant='contained'
          onClick={() => {
            setConversationId('');
          }}
        >
          <AddIcon
            sx={{
              width: '24px',
              height: '24px'
            }}
          />
          New Chat
        </Button>
      }
      historyButtonRef={historyBtnRef}
      icon={
        <AssistantOutlined
          sx={{
            width: '32px',
            height: '32px'
          }}
        />
      }
      open={open}
      subtitle={
        <>
          Chat initialized with thread ID:{' '}
          {conversationId ?? 'There was an error. Please try again'}
        </>
      }
      title='GX Go Assistant'
      onClose={onClose}
      onHistoryClick={handleHistoryClick}
    >
      {open && (
        <>
          <Chat />
          <MessageInput maxRows={3} />
          <ChatHistory
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
          />
        </>
      )}
    </SideDrawer>
  );
};

const AskGXDrawer = ({ open, onClose }: { open: boolean; onClose: MouseEventHandler }) => {
  return (
    <ChatProvider open={open}>
      <AskGXContent
        open={open}
        onClose={onClose}
      />
    </ChatProvider>
  );
};

export default AskGXDrawer;
